import { DockviewApi, DockviewPanelApi } from "dockview-react";
import React, { use, useCallback, useEffect } from "react";
import SafeNumberInput from "./SafeNumberInput";
import { addFloatingPanel } from "./utils/DockerviewSettings";
import { ChartLauncherModal } from "./utils/ChartLauncherModal";
import { CatalyzerItem, useCatalyzerStore } from "../stores/catalyzer";
import { api } from "./utils/api";
import { componentSettings } from "./utils/DockerviewSettings";
import { useReadAloud, ReadableItem } from "./utils/useReadAloud";
import { ReadAloudBar } from "./utils/ReadAloudBar";
import { cleanTextForSpeech } from "./utils/speechText";
import { useCtrlDMark } from "./utils/useCtrlDMark";
import { TickerLink } from "./TickerActionMenu";
import { HighlightedText, Mark } from "./utils/HighlightedText";

interface ITickerChange {
  premarket_change_rel: number | null;
  regular_hours_change_rel: number | null;
}

interface ITweet {
  url: string;
  source: string;
  published_at: number;
  content: string;
  content_marks: Mark[];
  read: number; // 0, 1, 2 (0 = not read, 1 = read, 2 = bookmarked)
  tickers: string[];
  ticker_changes: Record<string, ITickerChange>;
}

// Change indicator is jet black regardless of direction (no up/down color coding here).
const CHART_UP_COLOR = "#0a0a0a";
const CHART_DOWN_COLOR = "#0a0a0a";

function formatSignedPct(v: number): string {
  return `${v >= 0 ? "+" : ""}${v.toFixed(1)}%`;
}

interface ITweetsRequest {
  query: string;
  start_date: string;
  end_date: string;
  source: string;
  page: number;
  table: string;
  saved_only: boolean;
  reverse: boolean;
  has_tickers: boolean;
}

export const TweetsSearch = (props: {
  api: DockviewPanelApi;
  containerApi: DockviewApi;
}) => {
  const [tweets, setTweets] = React.useState<ITweet[]>([]);
  const [tweetsRequest, setTweetsRequest] = React.useState<ITweetsRequest>({
    query: "",
    start_date: "",
    end_date: "",
    page: 1,
    source: "",
    table: "tweets",
    saved_only: false,
    reverse: false,
    has_tickers: false,
  });
  const [errorMessage, setErrorMessage] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [catActive, setCatActive] = React.useState(false);

  const catalyzerStore = useCatalyzerStore();

  const readAloud = useReadAloud([tweets]);

  // auto fetch on mount
  useEffect(() => {
    getTweets();
  }, []);

  const readableItems: ReadableItem[] = tweets
    .filter((item) => item.read === 0)
    .map((tweet) => ({ key: tweet.url, texts: [cleanTextForSpeech(tweet.content).toLowerCase()] }));

  const onItemDone = useCallback((key: string) => {
    markTweet(key, 1);
  }, [tweets]);

  const handleCtlD = useCallback(() => {
    const firstUnread = tweets.find((item) => item.read === 0);
    if (firstUnread) markTweet(firstUnread.url, 1);
  }, [tweets]);

  const getNextScrollKey = useCallback(() => {
    const unread = tweets.filter((item) => item.read === 0);
    return unread[1]?.url ?? null;
  }, [tweets]);

  useCtrlDMark({ onTrigger: handleCtlD, getNextScrollKey, panelApi: props.api, containerApi: props.containerApi });

  function toggleCat() {
    setCatActive(!catActive);

    if (!catActive) {
      sendDataToCatalyzer(tweets);
    } else {
      catalyzerStore.setItems([]);
    }
  }

  function handleChartOpen(ticker: string, startDate: string, endDate: string, interval: string) {
    // Activate CAT on this component if not already active
    if (!catActive) {
      setCatActive(true);
      sendDataToCatalyzer(tweets);
    }
    // Open a new EquityChart panel with the params
    addFloatingPanel(props.containerApi, "equity_chart", { ticker, startDate, endDate, interval });
  }

  function sendDataToCatalyzer(tweets: ITweet[]) {
    // clean
    catalyzerStore.setItems([]);

    let items: CatalyzerItem[] = [];

    for (let tweet of tweets) {
      let [dateStr, dateObj] = unixTimestampToNewYork(tweet.published_at);

      // match the first url in anchor tag or plain text
      // @ts-ignore
      const m = tweet.content.match(
        /<a\b[^>]*\bhref\s*=\s*(['"])(?<url>[^"'<>]+)\1/i
      );
      const url = m?.groups?.url ?? null; // -> "https://t.co/CZ8KL2UYHA"

      items.push({
        nyTS: dateObj,
        stringDate: dateStr,
        text:
          tweet.content.slice(0, 600) + ` - Source Twitter(${tweet.source})`,
        url: url ?? "",
      });
    }

    catalyzerStore.setItems(items);
  }

  const markTweet = (url: string, read: number) => {
    const newMark = read % 3; // 0 = not read, 1 = read, 2 = bookmarked

    setTweets((prevTweets) =>
      prevTweets.map((item) =>
        item.url === url ? { ...item, read: newMark } : item
      )
    );

    api
      .post("/mark", { url, read: newMark, table: "tweets" })
      .then((response) => {
        const data = response.data;
        if (!data?.success) {
          console.error("data.success is false");
          // revert the change
          setTweets((prevTweets) =>
            prevTweets.map((item) =>
              item.url === url ? { ...item, read: read } : item
            )
          );
          throw new Error("Failed to mark news as read");
        }
      })
      .catch((error) => {
        // revert the change
        setTweets((prevTweets) =>
          prevTweets.map((item) =>
            item.url === url ? { ...item, read: read } : item
          )
        );
        console.error("Error marking news as read:", error);
      });
  };

  const getTweets = async () => {
    setErrorMessage("");
    setIsLoading(true);

    try {
      const response = await api.post("/search", tweetsRequest);
      setTweets(response.data);
      setIsLoading(false);

      // if catActive, set the active timestamps in the store
      if (catActive) {
        sendDataToCatalyzer(response.data);
      }
    } catch (error: any) {
      console.error(error?.message || error);
      setErrorMessage("Error while fetching tweets");
      setIsLoading(false);
    }

    // change title 
    if (props.api) {
      const title = "Tweets: " + tweetsRequest.query;
      props.api.setTitle(title);
    }
  };

  function unixTimestampToNewYork(timestamp: number): [string, Date] {
    const date = new Date(timestamp * 1000);
    // convert to New York time, format as 17 Apr 2025 HH:MM
    const newyorkDate: Date = new Date(
      date.toLocaleString("en-US", { timeZone: "America/New_York" })
    );

    const day = String(newyorkDate.getDate()).padStart(2, "0");
    const month = String(newyorkDate.getMonth() + 1).padStart(2, "0");
    const year = newyorkDate.getFullYear();
    const hours = String(newyorkDate.getHours()).padStart(2, "0");
    const minutes = String(newyorkDate.getMinutes()).padStart(2, "0");
    const seconds = String(newyorkDate.getSeconds()).padStart(2, "0");

    // get the day of the week
    const options: Intl.DateTimeFormatOptions = { weekday: "short" };
    const dayOfWeek = newyorkDate.toLocaleDateString("en-US", options);

    return [
      `${year}-${month}-${day} ${hours}:${minutes} (${dayOfWeek})`,
      newyorkDate,
    ];
  }

  return (
    <div className="relative flex flex-col h-full">
      <div className="flex-1 overflow-auto p-2">
      <div className="flex gap-4 mb-4">
        <button
          className={` mt-2 btn ${catActive ? "btn-primary" : "btn-default"}`}
          onClick={(e) => {
            e.preventDefault();
            toggleCat();
          }}
        >
          CAT
        </button>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            getTweets();
          }}
          className="flex flex-row gap-4"
        >
          <div className="form-group">
            <label htmlFor="page">Page:</label>
            <SafeNumberInput
              id="page"
              value={tweetsRequest.page}
              onChange={(value) =>
                setTweetsRequest({ ...tweetsRequest, page: value })
              }
              min={1}
              max={10000}
            />
          </div>

          <div className="form-group">
            <label htmlFor="query">Query:</label>
            <input
              type="text"
              id="query"
              value={tweetsRequest.query}
              onChange={(e) =>
                setTweetsRequest({ ...tweetsRequest, query: e.target.value })
              }
            />
          </div>

          <div className="form-group">
            <label htmlFor="start-date">Start date:</label>
            <input
              placeholder="YYYY-MM-DD"
              type="text"
              id="start-date"
              value={tweetsRequest.start_date}
              onChange={(e) =>
                setTweetsRequest({
                  ...tweetsRequest,
                  start_date: e.target.value,
                })
              }
            />
          </div>

          <div className="form-group">
            <label htmlFor="end-date">End date:</label>
            <input
              placeholder="YYYY-MM-DD"
              type="text"
              id="end-date"
              value={tweetsRequest.end_date}
              onChange={(e) =>
                setTweetsRequest({ ...tweetsRequest, end_date: e.target.value })
              }
            />
          </div>

          <div className="form-group">
            <label htmlFor="source">Source:</label>
            <select
              id="source"
              value={tweetsRequest.source}
              onChange={(e) => {
                setTweetsRequest({ ...tweetsRequest, source: e.target.value });
              }}
              className="border border-gray-300 rounded p-1 block w-full"
            >
              <option value="">All</option>
              <option value="FirstSquawk">FirstSquawk</option>
              <option value="TheInsiderPaper">TheInsiderPaper</option>
              <option value="notreload_ai">notreload_ai</option>
              <option value="Trade_The_News">Trade_The_News</option>
              <option value="LiveSquawk">LiveSquawk</option>
              <option value="CorleoneDon77">CorleoneDon77</option>
              <option value="wallstengine">wallstengine</option>
              <option value="DeItaone">DeItaone</option>
              <option value="OracleNYSE">OracleNYSE</option>
              <option value="tenet_research">tenet_research</option>
              <option value="MarketNews_Feed">MarketNews_Feed</option>
              <option value="CryptoMilox">CryptoMilox</option>
              <option value="SpacRight">SpacRight</option>
              <option value="LF94Trades">LF94Trades</option>
              <option value="faststocknewss">faststocknewss</option>
              <option value="blckchaindaily">blckchaindaily</option>
              <option value="NuntioBot">NuntioBot</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="saved_only">
              <input
                type="checkbox"
                id="saved_only"
                checked={tweetsRequest.saved_only}
                onChange={(e) =>
                  setTweetsRequest({
                    ...tweetsRequest,
                    saved_only: e.target.checked,
                  })
                }
              />
              &nbsp; Saved only
            </label>
          </div>

          <div className="form-group">
            <label htmlFor="reverse">
              <input
                type="checkbox"
                id="reverse"
                checked={tweetsRequest.reverse}
                onChange={(e) =>
                  setTweetsRequest({
                    ...tweetsRequest,
                    reverse: e.target.checked,
                  })
                }
              />
              &nbsp; Reverse
            </label>
          </div>

          <div className="form-group">
            <label htmlFor="has_tickers">
              <input
                type="checkbox"
                id="has_tickers"
                checked={tweetsRequest.has_tickers}
                onChange={(e) =>
                  setTweetsRequest({
                    ...tweetsRequest,
                    has_tickers: e.target.checked,
                  })
                }
              />
              &nbsp; Has tickers
            </label>
          </div>

          <button type="submit" className="hidden">
            Search
          </button>
        </form>
      </div>
      <TweetsTable
        api={props.api}
        containerApi={props.containerApi}
        tweets={tweets}
        errorMessage={errorMessage}
        isLoading={isLoading}
        searchTerm={tweetsRequest.query}
        unixTimestampToNewYork={unixTimestampToNewYork}
        markTweet={markTweet}
        currentlyReadingKey={readAloud.currentlyReadingKey}
        catActive={catActive}
        onChartOpen={handleChartOpen}
      />

      </div>

      <ReadAloudBar
        voiceControls={readAloud.voiceControls}
        setVoiceControls={readAloud.setVoiceControls}
        toggleReading={readAloud.toggleReading}
        skipItem={readAloud.skipItem}
        items={readableItems}
        onItemDone={onItemDone}
        idPrefix="tweets-"
        containerApi={props.containerApi}
        panelApi={props.api}
      />
    </div>
  );
};

const TweetsTable = (props: {
  api: DockviewPanelApi;
  containerApi: DockviewApi;
  tweets: ITweet[];
  errorMessage: string;
  isLoading: boolean;
  searchTerm: string;
  unixTimestampToNewYork: (timestamp: number) => [string, Date];
  markTweet: (url: string, read: number) => void;
  currentlyReadingKey: string;
  catActive: boolean;
  onChartOpen: (ticker: string, startDate: string, endDate: string, interval: string) => void;
}) => {
  const setHoveredItemIndex = useCatalyzerStore((s) => s.setHoveredItemIndex);
  const hoveredItemIndex = useCatalyzerStore((s) => s.hoveredItemIndex);
  const itemRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  const [chartModalPublishedAt, setChartModalPublishedAt] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (hoveredItemIndex !== null && itemRefs.current[hoveredItemIndex]) {
      itemRefs.current[hoveredItemIndex]!.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [hoveredItemIndex]);

  const [highlightedText, setHighlightedText] = React.useState<string[]>([]);

  useEffect(() => {
    // if multi word, pass array of words
    const searchTerm = props.searchTerm.trim();

    if (searchTerm) {
      setHighlightedText(searchTerm.split(/\s+/));
    } else {
      setHighlightedText([]);
    }
  }, [props.isLoading]);

  if (props.isLoading) {
    return <div className="text-center">Loading...</div>;
  }

  if (props.errorMessage !== "") {
    return <p className="text-center">{props.errorMessage}</p>;
  }

  if (props.tweets.length === 0) {
    return <p className="text-center">No tweets found</p>;
  }

  function addMarkClass(mark: number): string {
    switch (mark) {
      case 0:
        return "bg-gray-400";
      case 1:
        return "bg-blue-600";
      case 2:
        return "bg-yellow-600";
      default:
        return "bg-gray-400";
    }
  }

  return (
    <div className="mb-20">
      {props.tweets.map((tweet, index) => (
        <div
          key={tweet.url}
          ref={(el) => { itemRefs.current[index] = el; }}
          id={tweet.url}
          className={`mb-4 flex gap-4 ${props.currentlyReadingKey === tweet.url ? "bg-yellow-100" : ""} ${hoveredItemIndex === index ? "bg-yellow-100" : ""}`}
          onMouseEnter={() => { if (props.catActive) setHoveredItemIndex(index); }}
          onMouseLeave={() => { if (props.catActive) setHoveredItemIndex(null); }}
        >
          <div
            className={`min-w-4 cursor-pointer ${addMarkClass(tweet.read)}`}
            onClick={() => {
              props.markTweet(tweet.url, tweet.read + 1);
            }}
          ></div>

          <div className="gap-4">
            <p className="text-gray-500 gap-4 flex">
              <span>{props.unixTimestampToNewYork(tweet.published_at)[0]}</span>
              <a href={tweet.url} target="_blank" rel="noopener noreferrer">
                {tweet.source}
              </a>
              <a
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  setChartModalPublishedAt(tweet.published_at);
                }}
              >
                (chart)
              </a>
              <a
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  addFloatingPanel(props.containerApi, "correlate", { query: tweet.content });
                }}
              >
                (correlate)
              </a>
            </p>
            {tweet.tickers.length > 0 && (
              <p className="font-bold flex flex-wrap gap-x-1 items-baseline">
                {tweet.tickers.map((ticker) => {
                  const c = tweet.ticker_changes[ticker];
                  return (
                    <span key={ticker} className="whitespace-nowrap">
                      <TickerLink ticker={ticker} containerApi={props.containerApi} />
                      {c && c.premarket_change_rel !== null && c.regular_hours_change_rel !== null && (
                        <span className="ml-1 font-semibold tabular-nums">
                          (
                          <span style={{ color: c.premarket_change_rel >= 0 ? CHART_UP_COLOR : CHART_DOWN_COLOR }}>
                            {formatSignedPct(c.premarket_change_rel)}
                          </span>{" "}
                          <span style={{ color: c.regular_hours_change_rel >= 0 ? CHART_UP_COLOR : CHART_DOWN_COLOR }}>
                            {formatSignedPct(c.regular_hours_change_rel)}
                          </span>
                          )
                        </span>
                      )}
                    </span>
                  );
                })}
              </p>
            )}
            <div className="whitespace-pre-wrap">
              <HighlightedText text={tweet.content} marks={tweet.content_marks} />
            </div>
          </div>
          <hr />
        </div>
      ))}

      {chartModalPublishedAt !== null && (
        <ChartLauncherModal
          publishedAt={chartModalPublishedAt}
          onSubmit={props.onChartOpen}
          onClose={() => setChartModalPublishedAt(null)}
        />
      )}
    </div>
  );
};
